import React, { useState, useEffect, useCallback } from 'react';
import { Star, Pencil, Trash2, Send } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { reviewsAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

function StarRating({ value, onChange, size = 'md', readOnly = false }) {
    const [hover, setHover] = useState(0);
    const sizes = { sm: 'w-3.5 h-3.5', md: 'w-5 h-5', lg: 'w-6 h-6' };
    return (
        <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map(star => (
                <button
                    key={star}
                    type="button"
                    disabled={readOnly}
                    className={cn("transition-colors", !readOnly && "cursor-pointer hover:scale-110")}
                    onMouseEnter={() => !readOnly && setHover(star)}
                    onMouseLeave={() => !readOnly && setHover(0)}
                    onClick={() => !readOnly && onChange?.(star)}
                    data-testid={`star-${star}`}
                >
                    <Star className={cn(
                        sizes[size],
                        (hover || value) >= star ? "fill-amber-400 text-amber-400" : "text-zinc-600"
                    )} />
                </button>
            ))}
        </div>
    );
}

function RatingBar({ stars, count, total }) {
    const pct = total > 0 ? (count / total) * 100 : 0;
    return (
        <div className="flex items-center gap-2 text-sm">
            <span className="w-3 text-muted-foreground">{stars}</span>
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-6 text-xs text-muted-foreground text-right">{count}</span>
        </div>
    );
}

export function ReviewSection({ productId }) {
    const { isAuthenticated, user } = useAuth();
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0, distribution: {} });
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(false);
    const [rating, setRating] = useState(0);
    const [text, setText] = useState('');
    const [loading, setLoading] = useState(false);

    const fetchReviews = useCallback(async () => {
        try {
            const res = await reviewsAPI.getProductReviews(productId);
            setReviews(res.data.reviews);
            setStats(res.data.stats);
        } catch { /* ignore */ }
    }, [productId]);

    useEffect(() => { fetchReviews(); }, [fetchReviews]);

    const myReview = reviews.find(r => r.user_id === user?.id);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (rating === 0) { toast.error('Kérlek válassz értékelést'); return; }
        if (!text.trim()) { toast.error('Kérlek írj véleményt'); return; }
        setLoading(true);
        try {
            if (editing) {
                await reviewsAPI.updateReview(productId, { rating, text: text.trim() });
                toast.success('Vélemény frissítve!');
            } else {
                await reviewsAPI.createReview(productId, { rating, text: text.trim() });
                toast.success('Vélemény elküldve!');
            }
            setShowForm(false);
            setEditing(false);
            setRating(0);
            setText('');
            fetchReviews();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Hiba történt');
        }
        setLoading(false);
    };

    const handleEdit = () => {
        if (myReview) {
            setRating(myReview.rating);
            setText(myReview.text);
            setEditing(true);
            setShowForm(true);
        }
    };

    const handleDelete = async (reviewId) => {
        if (!window.confirm('Biztosan törlöd a véleményt?')) return;
        try {
            await reviewsAPI.deleteReview(productId, reviewId);
            toast.success('Vélemény törölve');
            fetchReviews();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Hiba történt');
        }
    };

    const formatDate = (d) => new Date(d).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });
    const isAdmin = user?.role === 'admin';

    return (
        <div className="space-y-6" data-testid="review-section">
            <h2 className="text-lg font-semibold">Vélemények és értékelések</h2>

            {/* Stats summary */}
            <div className="flex flex-col sm:flex-row gap-6 bg-card border rounded-lg p-5">
                <div className="flex flex-col items-center justify-center min-w-[120px]">
                    <span className="text-4xl font-bold">{stats.average_rating || '—'}</span>
                    <StarRating value={Math.round(stats.average_rating)} readOnly size="sm" />
                    <span className="text-xs text-muted-foreground mt-1">{stats.total_reviews} értékelés</span>
                </div>
                <div className="flex-1 space-y-1">
                    {[5, 4, 3, 2, 1].map(s => (
                        <RatingBar key={s} stars={s} count={parseInt(stats.distribution?.[String(s)] || 0)} total={stats.total_reviews} />
                    ))}
                </div>
            </div>

            {/* Write review button */}
            {isAuthenticated && !showForm && (
                <div>
                    {myReview ? (
                        <Button variant="outline" size="sm" onClick={handleEdit} data-testid="edit-review-btn">
                            <Pencil className="w-4 h-4 mr-2" /> Véleményem szerkesztése
                        </Button>
                    ) : (
                        <Button size="sm" onClick={() => { setShowForm(true); setEditing(false); setRating(0); setText(''); }} className="bg-cyan-600 hover:bg-cyan-500" data-testid="write-review-btn">
                            <Star className="w-4 h-4 mr-2" /> Vélemény írása
                        </Button>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">Csak megvásárolt termékekhez írhatsz véleményt.</p>
                </div>
            )}

            {/* Review form */}
            {showForm && (
                <form onSubmit={handleSubmit} className="bg-card border rounded-lg p-5 space-y-4" data-testid="review-form">
                    <div>
                        <label className="text-sm font-medium mb-2 block">Értékelés</label>
                        <StarRating value={rating} onChange={setRating} size="lg" />
                    </div>
                    <div>
                        <label className="text-sm font-medium mb-2 block">Vélemény</label>
                        <textarea
                            value={text}
                            onChange={e => setText(e.target.value)}
                            placeholder="Írd le a tapasztalataidat..."
                            rows={4}
                            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
                            required
                            data-testid="review-text-input"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => { setShowForm(false); setEditing(false); }}>
                            Mégse
                        </Button>
                        <Button type="submit" size="sm" disabled={loading} className="bg-cyan-600 hover:bg-cyan-500" data-testid="review-submit-btn">
                            {loading ? 'Küldés...' : <><Send className="w-4 h-4 mr-2" /> {editing ? 'Frissítés' : 'Küldés'}</>}
                        </Button>
                    </div>
                </form>
            )}

            {/* Reviews list */}
            {reviews.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4">Még nincsenek vélemények ehhez a termékhez.</p>
            ) : (
                <div className="space-y-3">
                    {reviews.map(review => (
                        <div key={review.id} className="bg-card border rounded-lg p-4" data-testid={`review-${review.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">{review.user_name}</span>
                                        <StarRating value={review.rating} readOnly size="sm" />
                                    </div>
                                    <p className="text-xs text-muted-foreground mt-0.5">{formatDate(review.created_at)}</p>
                                </div>
                                <div className="flex gap-1">
                                    {review.user_id === user?.id && (
                                        <button onClick={handleEdit} className="p-1.5 rounded hover:bg-secondary" data-testid="review-edit-btn">
                                            <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                                        </button>
                                    )}
                                    {(review.user_id === user?.id || isAdmin) && (
                                        <button onClick={() => handleDelete(review.id)} className="p-1.5 rounded hover:bg-destructive/10" data-testid="review-delete-btn">
                                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <p className="text-sm mt-2">{review.text}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export { StarRating };
